import { Crown, Lock, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

interface UpgradeRequiredProps {
  feature: "tableMaps" | "whatsapp" | "emailCampaigns";
  currentPlan: string;
}

const FEATURE_INFO = {
  tableMaps: {
    title: "Mapa de Mesas",
    description:
      "Diseña visualmente la distribución de tu restaurante. Organiza salas, mesas y zonas para gestionar mejor tus reservas.",
    benefits: [
      "Editor visual drag & drop",
      "Organización por salas y zonas",
      "Asignación automática de mesas",
      "Objetos decorativos (barra, baños, puertas)",
      "Vista en tiempo real de ocupación",
    ],
    icon: "🪑",
  },
  whatsapp: {
    title: "Notificaciones WhatsApp",
    description:
      "Envía confirmaciones y recordatorios de reserva automáticamente por WhatsApp para mejorar la asistencia.",
    benefits: [
      "Confirmación automática de reserva",
      "Recordatorios 24h antes",
      "Notificaciones de cambios",
      "Mejora la tasa de asistencia",
      "Comunicación directa con clientes",
    ],
    icon: "📱",
  },
  emailCampaigns: {
    title: "Campañas de Email",
    description:
      "Envía newsletters, promociones y comunicaciones masivas a tus clientes para fidelizarlos y aumentar tus reservas.",
    benefits: [
      "Plantillas profesionales listas para usar",
      "Segmentación de audiencia (VIP, recientes, etc.)",
      "Programación de envíos",
      "Estadísticas de apertura y clics",
      "Usa tu propio servidor SMTP",
    ],
    icon: "📧",
  },
};

export default function UpgradeRequired({
  feature,
  currentPlan,
}: UpgradeRequiredProps) {
  const info = FEATURE_INFO[feature];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-8 py-10 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-2xl mb-4">
              <span className="text-4xl">{info.icon}</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              {info.title}
            </h1>
            <p className="text-primary-100 text-sm md:text-base max-w-md mx-auto">
              Esta funcionalidad no está disponible en tu plan actual
            </p>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Current Plan Badge */}
            <div className="flex items-center justify-center mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-600">
                <Lock className="w-4 h-4" />
                Tu plan actual:{" "}
                <strong className="text-gray-900">{currentPlan}</strong>
              </span>
            </div>

            {/* Description */}
            <p className="text-gray-600 text-center mb-8">{info.description}</p>

            {/* Benefits */}
            <div className="bg-gradient-to-br from-primary-50 to-green-50 rounded-xl p-6 mb-8">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary-600" />
                Lo que incluye esta función:
              </h3>
              <ul className="space-y-3">
                {info.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center mt-0.5">
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </span>
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA */}
            <div className="text-center space-y-4">
              <Link
                to="/subscription"
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all shadow-lg hover:shadow-xl"
              >
                <Crown className="w-5 h-5" />
                Mejorar mi plan
                <ArrowRight className="w-5 h-5" />
              </Link>

              <p className="text-sm text-gray-500">
                Actualiza a un plan superior para desbloquear todas las
                funcionalidades
              </p>
            </div>
          </div>
        </div>

        {/* Help text */}
        <p className="text-center text-sm text-gray-500 mt-6">
          ¿Tienes dudas? Contacta con nosotros en{" "}
          <a
            href="mailto:reservamanager@lucrapp.com"
            className="text-primary-600 hover:underline"
          >
            reservamanager@lucrapp.com
          </a>
        </p>
      </div>
    </div>
  );
}
